import React, { useRef, useState, useEffect } from 'react';
import Alert from "@amzn/awsui-components-core/alert";
import Badge from "@amzn/awsui-components-core/badge";
import Box from "@amzn/awsui-components-core/box";
import Button from "@amzn/awsui-components-core/button";
import Checkbox from "@amzn/awsui-components-core/checkbox";
import FileDropzone, { useFilesDragging } from "@amzn/awsui-components-core/file-dropzone";
import FileTokenGroup from "@amzn/awsui-components-core/file-token-group";
import FileUpload from "@amzn/awsui-components-core/file-upload";
import FormField from "@amzn/awsui-components-core/form-field";
import Icon from "@amzn/awsui-components-core/icon";
import Input from "@amzn/awsui-components-core/input";
import Link from "@amzn/awsui-components-core/link";
import Modal from "@amzn/awsui-components-core/modal";
import Multiselect from "@amzn/awsui-components-core/multiselect";
import Pagination from "@amzn/awsui-components-core/pagination";
import SpaceBetween from "@amzn/awsui-components-core/space-between";
import StatusIndicator from "@amzn/awsui-components-core/status-indicator";
import Spinner from "@amzn/awsui-components-core/spinner";
import Table from "@amzn/awsui-components-core/table";
import { fetchAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement, getUploadUrl, getDownloadUrl, acknowledgeAnnouncement, fetchAcknowledgements, AnnouncementEntry, Attachment, FileMetadata, AcknowledgementSummary, UserAcknowledgement } from 'src/services/announcements-api';
import { useUser } from 'src/context/UserContext';
import FormattedDate from 'src/components/shared/FormattedDate';
import { getErrorMessage } from 'src/utils/errorMessages';
import { useAlertMessages } from 'src/utils/useAlertMessages';
import RichTextEditor from 'src/components/shared/RichTextEditor';
import RichTextContent from 'src/components/shared/RichTextContent';
import { SERVICE_LINES } from 'src/constants';
import { getDefaultVisibility, getAllowedVisibility } from 'src/roles-permissions';
import { greenButtonStyles, removeIconStyles, tagStyles } from 'src/components/shared/design-tokens';
import ActionButtons from 'src/components/shared/ActionButtons';
import { requiredMark, requiredFieldHint } from 'src/components/shared/formHelpers';
import './Announcements.scss';
import mockData from 'src/mock/mockData.json';

const USE_MOCK_DATA = false;

interface AnnouncementsProps {
    /**
     * When set, always show exactly this many announcements by default (before
     * "View more"), bypassing the height-based measurement. Used for the bottom
     * placement (supervisors/admins) where the panel isn't height-constrained.
     */
    defaultVisibleCount?: number;
}

const Announcements: React.FC<AnnouncementsProps> = ({ defaultVisibleCount }) => {
    const { user, canWrite, canModify } = useUser();
    const canEdit = canWrite('announcements');
    // Base service-line users see Announcements in the left panel (Dashboard renders it
    // without defaultVisibleCount). In that placement, show the "Last updated" info on
    // its own line below the title/created row; otherwise keep it inline.
    const stackLastUpdated = defaultVisibleCount == null;
    const defaultVisibility = getDefaultVisibility(user?.permissions || []);
    const allowedVisibility = getAllowedVisibility(user?.permissions || []);
    const visibilityOptions = SERVICE_LINES
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(opt => ({
            label: opt.name,
            value: opt.value,
            disabled: allowedVisibility.length > 0 && !allowedVisibility.includes(opt.value),
        }));
    const [announcements, setAnnouncements] = useState<AnnouncementEntry[]>([]);
    const [acknowledgeRequired, setAcknowledgeRequired] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [editingId, setEditingId] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [bodyJson, setBodyJson] = useState<Record<string, any> | undefined>(undefined);
    const [files, setFiles] = useState<File[]>([]);
    const [targetGroups, setTargetGroups] = useState<string[]>([]);
    const [existingAttachments, setExistingAttachments] = useState<Attachment[]>([]);
    const { error, successMsg, setError, setSuccessMsg, alertRef } = useAlertMessages();
    const firstInputRef = useRef<any>(null);
    const sectionRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(Infinity); // Start showing all, then trim after measurement

    // Acknowledgement state
    const [userAcknowledged, setUserAcknowledged] = useState<Record<string, UserAcknowledgement>>({});
    const [acknowledgementsMap, setAcknowledgementsMap] = useState<Record<string, AcknowledgementSummary[]>>({});
    const [ackModalTarget, setAckModalTarget] = useState<{ id: string; title: string } | null>(null);
    const [ackModalData, setAckModalData] = useState<AcknowledgementSummary[]>([]);
    const [ackModalLoading, setAckModalLoading] = useState(false);
    const [ackModalPage, setAckModalPage] = useState(1);
    const [acknowledgingId, setAcknowledgingId] = useState<string | null>(null);

    useEffect(() => { loadAnnouncements(); }, []);

    useEffect(() => {
        if (showForm && firstInputRef.current) {
            setTimeout(() => firstInputRef.current?.focus(), 100);
        }
    }, [showForm]);

    // Dynamically calculate how many announcements fit in the available space
    useEffect(() => {
        if (loading || announcements.length === 0 || showAll) return;

        // Fixed-count mode: skip height measurement and show a fixed number.
        if (defaultVisibleCount != null) {
            setVisibleCount(Math.min(defaultVisibleCount, announcements.length));
            return;
        }

        const calculateVisibleCount = () => {
            const bodyEl = bodyRef.current;
            const listEl = listRef.current;
            if (!bodyEl || !listEl) return;

            // The body element fills the available flex space (1000px minus header)
            const availableHeight = bodyEl.clientHeight;
            if (availableHeight === 0) {
                // No layout available (e.g. test env) — fall back to showing all
                setVisibleCount(announcements.length);
                return;
            }

            const items = listEl.querySelectorAll('.announcement-entry');
            if (items.length === 0) return;

            let totalHeight = 0;
            let count = 0;
            const gap = 12; // gap between items
            const viewMoreBuffer = 50; // space for "View more" button

            for (let i = 0; i < items.length; i++) {
                const itemHeight = (items[i] as HTMLElement).offsetHeight + (i > 0 ? gap : 0);
                if (totalHeight + itemHeight > availableHeight - viewMoreBuffer) break;
                totalHeight += itemHeight;
                count++;
            }

            // If all items fit, show them all (no "View more" needed)
            // Otherwise show only what fits
            const newCount = count >= announcements.length ? announcements.length : Math.max(1, count);
            setVisibleCount(newCount);
        };

        // Use requestAnimationFrame to ensure layout is complete before measuring
        const raf = requestAnimationFrame(() => {
            setTimeout(calculateVisibleCount, 50);
        });
        return () => cancelAnimationFrame(raf);
    }, [loading, announcements, showAll, visibleCount, defaultVisibleCount]);

    const loadAnnouncements = async () => {
        setLoading(true);
        try {
            if (USE_MOCK_DATA) {
                setAnnouncements((mockData.announcements.items || []) as AnnouncementEntry[]);
                setExpandedIds(new Set(mockData.announcements.items.map(i => i.announcementId)));
                setUserAcknowledged(mockData.announcements.userAcknowledged || {});
                if (mockData.announcements.acknowledgementsMap) {
                    setAcknowledgementsMap(mockData.announcements.acknowledgementsMap);
                }
            } else {
                const data = await fetchAnnouncements();
                setAnnouncements(data.items || []);
                const items = (data.items || []) as AnnouncementEntry[];
                setExpandedIds(new Set(items.map(i => i.announcementId)));
                setUserAcknowledged(data.userAcknowledged || {});
                if (data.acknowledgementsMap) {
                    setAcknowledgementsMap(data.acknowledgementsMap);
                }
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // const handleFileAttached = (e: any) => {
    //     const selected: File[] = Array.from(e.detail?.files || []);
    //     console.log("File Selected", selected)
    //     const invalid = selected.filter(f => f.type !== 'application/pdf');
    //     if (invalid.length > 0) {
    //         setError('Only PDF files are allowed');
    //         return;
    //     }
    //     setFiles(selected);
    // };

    const handleEdit = (item: AnnouncementEntry) => {
        setEditingId(item.announcementId);
        setTitle(item.title);
        setBody(item.body);
        setBodyJson(item.bodyJson);
        setFiles([]);
        setExistingAttachments(item.attachments || []);
        setTargetGroups(item.targetGroups || []);
        setAcknowledgeRequired(item.acknowledgementRequired ?? false);
        setShowForm(true);
    };

    const handleDeleteClick = (item: AnnouncementEntry) => {
        setDeleteTarget({ id: item.announcementId, title: item.title });
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteAnnouncement(deleteTarget.id);
            setSuccessMsg('Announcement deleted');
            await loadAnnouncements();
        } catch (err: any) {
            setError(err.message || 'Failed to delete');
        }
        setDeleteTarget(null);
    };

    // Upload the raw file bytes directly to S3 via a presigned PUT URL, then
    // return only small metadata to include in the create/update request body.
    // The file bytes never traverse CloudFront / Lambda@Edge / API Gateway, so
    // the edge-signed request stays below the Lambda@Edge body limit.
    const uploadFiles = async (uploadId: string): Promise<FileMetadata[]> => {
        return Promise.all(
            files.map(async (file): Promise<FileMetadata> => {
                const { uploadUrl, fileKey } = await getUploadUrl(uploadId, file.name, file.type);
                // Plain fetch straight to the presigned S3 URL. The presigned URL
                // is self-authorizing, so this request MUST NOT carry the app's
                // auth/role headers (getRequestHeaders) — only Content-Type.
                const putRes = await fetch(uploadUrl, {
                    method: 'PUT',
                    body: file,
                    headers: { 'Content-Type': file.type },
                });
                if (!putRes.ok) {
                    throw new Error(`Failed to upload ${file.name}`);
                }
                return {
                    fileName: file.name,
                    contentType: file.type,
                    fileKey,
                    size: file.size,
                };
            })
        );
    };

    const handleSubmit = async () => {
        if (!title || !body) return;
        const isEdit = !!editingId;
        setSubmitting(true);
        try {
            if (editingId) {
                const fileUploads = await uploadFiles(editingId);
                await updateAnnouncement(editingId, {
                    title,
                    body,
                    bodyJson,
                    targetGroups: targetGroups.length > 0 ? targetGroups : undefined,
                    acknowledgementRequired: acknowledgeRequired,
                    keepAttachments: existingAttachments.map(a => a.fileKey),
                    files: fileUploads.length > 0 ? fileUploads : undefined,
                });
                setSuccessMsg('Announcement updated');
            } else {
                // For create there is no announcement id yet. The backend derives
                // the S3 fileKey independently of this id (server-side UUID), so a
                // client-generated id is sufficient to key the upload request.
                const uploadId = crypto.randomUUID();
                const fileUploads = await uploadFiles(uploadId);
                await createAnnouncement({ title, body, bodyJson, targetGroups: targetGroups.length > 0 ? targetGroups : undefined, acknowledgementRequired: acknowledgeRequired, files: fileUploads });
                setSuccessMsg('Announcement posted successfully');
            }
            setShowForm(false);
            setEditingId(null);
            setTitle('');
            setBody('');
            setBodyJson(undefined);
            setFiles([]);
            setTargetGroups([]);
            setExistingAttachments([]);
            setAcknowledgeRequired(false);
            // Refresh in place (same as the create flow). On edit, briefly delay the
            // refetch so the write has time to propagate (read-after-write
            // consistency) before we re-read — otherwise a freshly toggled field
            // (e.g. acknowledgement required) can come back stale and the
            // acknowledgement section won't render. The list shows a loading spinner
            // during the wait since setLoading(true) is set here and loadAnnouncements
            // keeps it on until the refetch resolves.
            if (isEdit) {
                setLoading(true);
                await new Promise(resolve => setTimeout(resolve, 800));
            }
            await loadAnnouncements();
        } catch (err: any) {
            setError(getErrorMessage(err, 'Failed to save announcement.'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDownload = async (a: AnnouncementEntry, att: Attachment) => {
        try {
            const result = await getDownloadUrl(a.announcementId, att.fileKey);
            console.log("DOWNLOAD URL RESULT: ", result);
            window.open(result.downloadUrl, '_blank');
        } catch (err: any) {
            setError(err.message);
        }
    };

    const handleAcknowledge = async (announcementId: string) => {
        setAcknowledgingId(announcementId);
        try {
            const ack = await acknowledgeAnnouncement(announcementId);
            // Optimistic update
            setUserAcknowledged(prev => ({
                ...prev,
                [announcementId]: { acknowledged: true, acknowledgedAt: ack.acknowledgedAt },
            }));
            if (canEdit) {
                setAcknowledgementsMap(prev => ({
                    ...prev,
                    [announcementId]: [...(prev[announcementId] || []), ack],
                }));
            }
        } catch (err: any) {
            setError(err.message || 'Failed to acknowledge');
        } finally {
            setAcknowledgingId(null);
        }
    };

    const handleViewAcknowledgements = async (item: AnnouncementEntry) => {
        setAckModalTarget({ id: item.announcementId, title: item.title });
        setAckModalLoading(true);
        setAckModalPage(1);
        try {
            const result = await fetchAcknowledgements(item.announcementId);
            setAckModalData(result.acknowledgements || []);
        } catch (err: any) {
            setAckModalData(acknowledgementsMap[item.announcementId] || []);
        } finally {
            setAckModalLoading(false);
        }
    };

    const { areFilesDragging } = useFilesDragging();

    return (
        <div className="section-card" ref={sectionRef}>
            <div className="section-card__header">
                <h3 className="section-card__title">Announcements</h3>
                {!showForm && canEdit && (
                    <Button 
                        variant="primary" 
                        style={greenButtonStyles}
                        onClick={() => { 
                            setTargetGroups(defaultVisibility); 
                            setShowForm(true); 
                        }} 
                    > 
                    New Announcement
                    </Button>
                )}
            </div>

            <div className="section-card__body" ref={bodyRef}>
                <div ref={alertRef} tabIndex={-1}>
                    {error && 
                        <div className="alert">
                            <Alert
                                dismissible
                                type="error"
                                onDismiss={() => setError(null)}
                            >
                                <strong>Error: </strong>{error}
                            </Alert>
                        </div>
                    }
                    {successMsg && 
                        <div className="alert">
                            <Alert
                                dismissible
                                type="success"
                                onDismiss={() => setSuccessMsg(null)}
                            >
                                {successMsg}
                            </Alert>
                        </div>
                    }
                </div>

                {showForm && (
                    <div className="announcement-form">
                        <FormField label={<>Title{requiredMark()}</>}>
                            <Input 
                                placeholder="Announcement title" 
                                value={title} 
                                ref={firstInputRef} 
                                onChange={({ detail }) => setTitle(detail.value)} 
                            />
                        </FormField>

                        <RichTextEditor
                            label="Body"
                            required
                            value={bodyJson ?? body}
                            placeholder={"Announcement details..."} 
                            onChange={(html) => setBody(html)}
                            onChangeJson={(json) => setBodyJson(json)}
                        />

                        <div className="announcement-form__alignment">
                            <div className="announcement-form__multiselect">
                                <FormField label={<>Visibility{requiredMark()}</>} stretch>
                                    <Multiselect
                                        placeholder="Select Visibility"
                                        options={visibilityOptions}
                                        selectedOptions={visibilityOptions.filter(o => targetGroups.includes(o.value))}
                                        onChange={({ detail }) => { 
                                            setTargetGroups(detail.selectedOptions.map(obj => obj.value as string)); 
                                        }}
                                        enableSelectAll
                                        // tokenLimit={0}
                                        i18nStrings={{
                                            selectAllText: "Select all",
                                            // tokenLimitShowMore: "Show more",
                                            // tokenLimitShowFewer: "Show fewer",
                                        }}
                                    />
                                </FormField>
                            </div>
                            <div style={{ paddingTop: '30px' }}>
                                <Checkbox
                                    onChange={({ detail }) =>
                                        setAcknowledgeRequired(detail.checked)
                                    }
                                    checked={acknowledgeRequired}
                                >
                                    Require Acknowledgement
                                </Checkbox>
                            </div>
                        </div>

                        <div className="announcement-form__file">
                            <div>
                                <FormField label="Attachments" />
                                <SpaceBetween size="s">
                                    <div className="announcement-form__attachment">
                                        <FormField>
                                            <FileUpload
                                                // data-id="upload"
                                                onChange={({ detail }) => {
                                                    const selected: File[] = detail.value;
                                                    const invalid = selected.filter(f => f.type !== 'application/pdf');
                                                    if (invalid.length > 0) {
                                                        setError('Only PDF files are allowed');
                                                        return;
                                                    }
                                                    setFiles(prev => [...prev, ...selected]);
                                                }}
                                                value={[]}   // always empty — tokens shown in FileTokenGroup below
                                                i18nStrings={{
                                                    uploadButtonText: e => e ? "Upload files" : "Upload file",
                                                    dropzoneText: e => e ? "Drop files to upload" : "Drop file to upload",
                                                    removeFileAriaLabel: e => `Remove file ${e + 1}`,
                                                    limitShowFewer: "Show fewer files",
                                                    limitShowMore: "Show more files",
                                                    errorIconAriaLabel: "Error",
                                                    warningIconAriaLabel: "Warning"
                                                }}
                                                accept=".pdf,application/pdf"
                                                multiple
                                            />
                                        </FormField>
                                        <FileDropzone
                                            onChange={({ detail }) => {
                                                const selected: File[] = detail.value;
                                                const invalid = selected.filter(f => f.type !== 'application/pdf');
                                                if (invalid.length > 0) {
                                                    setError('Only PDF files are allowed');
                                                    return;
                                                }
                                                setFiles(prev => [...prev, ...selected]);
                                            }}
                                        >
                                            {areFilesDragging ? <>
                                                    Drop files here
                                                    <br />
                                                    <small>Format: PDF (max file size is 4 MB)</small>
                                                </> : (
                                                <>
                                                    Drag files here
                                                    <br />
                                                    <small>Format: PDF (max file size is 4 MB)</small>
                                                </>
                                            )}
                                        </FileDropzone>
                                    </div>
                                    <div className="announcement-form__file-tokens">
                                        <FileTokenGroup
                                            items={files.map(file => ({ file }))}
                                            onDismiss={({ detail }) =>
                                                setFiles(prev =>
                                                    prev.filter((_, index) => index !== detail.fileIndex)
                                                )
                                            }
                                            i18nStrings={{
                                                removeFileAriaLabel: (fileIndex) => `Remove file ${fileIndex + 1}`,
                                                limitShowFewer: "Show fewer files",
                                                limitShowMore: "Show more files",
                                                errorIconAriaLabel: "Error",
                                                warningIconAriaLabel: "Warning",
                                            }}
                                            showFileSize
                                        />
                                    </div>
                                </SpaceBetween>
                            </div>
                            {existingAttachments.length > 0 && (
                                <FileTokenGroup
                                    items={existingAttachments.map(att => ({
                                        file: new File([new ArrayBuffer(att.size || 0)], att.fileName),
                                    }))}
                                    onDismiss={({ detail }) => {
                                        setExistingAttachments(prev => prev.filter((_, i) => i !== detail.fileIndex));
                                    }}
                                    alignment="horizontal"
                                    showFileSize
                                />
                            )}
                        </div>
                        <div className="announcement-form__actions">
                            {requiredFieldHint()}
                            <div className="announcement-form__actions-buttons">
                                <Button 
                                    variant="link" 
                                    onClick={() => { 
                                        setShowForm(false); 
                                        setEditingId(null); 
                                        setTitle(''); 
                                        setBody(''); 
                                        setBodyJson(undefined); 
                                        setFiles([]); 
                                        setTargetGroups([]); 
                                        setExistingAttachments([]); 
                                        setAcknowledgeRequired(false); 
                                    }} 
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleSubmit}
                                    disabled={submitting || !title || !body || targetGroups.length === 0}
                                >
                                    {editingId ? 'Update' : 'Submit'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {loading && <Spinner />}

                {!loading && announcements.length === 0 && !showForm && (
                    <p className="section-card__placeholder">No announcements yet.</p>
                )}

                {!loading && announcements.length > 0 && (
                    <div className="announcement-list" ref={listRef}>
                        {(showAll ? announcements : announcements.slice(0, visibleCount)).map((item, idx) => {
                            const isExpanded = expandedIds.has(item.announcementId);
                            const attachmentsEl = item.attachments && item.attachments.length > 0 ? (
                                <div className="announcement-entry__attachments" data-size="m">
                                    {item.attachments.map((att, i) => (
                                        <Button
                                            key={i}
                                            variant="inline-link"
                                            onClick={() => handleDownload(item, att)}
                                        >
                                            <Icon name="file" size="small" /> {att.fileName}
                                        </Button>
                                    ))}
                                </div>
                            ) : null;
                            return (
                                <div key={idx} className="announcement-entry">
                                    {item.acknowledgementRequired && (
                                        <div className="announcement-entry__ack-warning">
                                            <Icon name="status-warning" size="small" variant="warning" />
                                            <span>Requires Acknowledgement</span>
                                        </div>
                                    )}
                                    <h4 className="announcement-entry__header">
                                        {item.title}
                                        <span className="announcement-entry__meta">
                                            <FormattedDate date={item.createdAt} /> &middot; by {item.createdBy}
                                            {!stackLastUpdated && item.updatedAt && item.updatedAt !== item.createdAt && (
                                                <span className="announcement-entry__last-updated-info"> | Last updated on <FormattedDate date={item.updatedAt} /> &middot; by {item.updatedBy}</span>
                                            )}
                                        </span>
                                        <div className="announcement-entry__header-right">
                                            {canModify('announcements', item.createdBy) && (
                                                <ActionButtons
                                                    onEdit={() => handleEdit(item)}
                                                    onDelete={() => handleDeleteClick(item)}
                                                    disabled={showForm}
                                                />
                                            )}
                                            <span
                                                className="announcement-entry__toggle"
                                                onClick={() => setExpandedIds(prev => {
                                                const next = new Set(prev);
                                                if (next.has(item.announcementId)) next.delete(item.announcementId);
                                                else next.add(item.announcementId);
                                                return next;
                                            })}
                                            >
                                                <Icon
                                                    name={isExpanded ? "caret-up-filled" : "caret-down-filled"}
                                                    variant="subtle"
                                                />
                                            </span>
                                        </div>
                                    </h4>
                                    {stackLastUpdated && item.updatedAt && item.updatedAt !== item.createdAt && (
                                        <div className="announcement-entry__last-updated announcement-entry__last-updated-info">
                                            Last updated on <FormattedDate date={item.updatedAt} /> &middot; by {item.updatedBy}
                                        </div>
                                    )}
                                    {!isExpanded && (
                                        <p className="announcement-entry__body announcement-entry__body--preview">{item.body.replace(/<[^>]*>/g, '')}</p>
                                    )}
                                    {isExpanded && (
                                        <>
                                            <div className="announcement-entry__body">
                                                <RichTextContent json={item.bodyJson} html={item.body} />
                                            </div>
                                            {item.acknowledgementRequired && attachmentsEl}
                                            <div className="announcement-entry__footer">
                                                {!item.acknowledgementRequired && attachmentsEl}
                                                {item.acknowledgementRequired && (
                                                    <div className="announcement-entry__acknowledgement-section">
                                                        {/* All users: acknowledge status */}
                                                        {userAcknowledged[item.announcementId]?.acknowledged
                                                            ? <StatusIndicator type="success">
                                                                <span className="announcement-entry__acknowledgement">
                                                                    Acknowledged by you on {new Date(userAcknowledged[item.announcementId].acknowledgedAt!).toLocaleString()}
                                                                </span>
                                                            </StatusIndicator>
                                                            : acknowledgingId === item.announcementId
                                                                ? <StatusIndicator type="pending">
                                                                    Acknowledging...
                                                                </StatusIndicator>
                                                                : <>
                                                                    <span className="ack-button">
                                                                        <Button
                                                                            // style={{ root: { ...greenButtonStyles.root, transform: "scale(0.85)" } }}
                                                                            style={ greenButtonStyles }
                                                                            onClick={() => handleAcknowledge(item.announcementId)}
                                                                        >
                                                                            Acknowledge
                                                                        </Button>
                                                                    </span>
                                                                    <span className="announcement-entry__acknowledgement announcement-entry__acknowledgement-text">I verify I have read this message</span>
                                                                </>
                                                        }
                                                        {/* Editors (supervisor/admin): always see acknowledgement count, regardless of their own status */}
                                                        {canEdit && (
                                                            <>
                                                            | <Link
                                                                onFollow={(e: any) => { e.preventDefault(); handleViewAcknowledgements(item); }}
                                                                variant="secondary"
                                                                fontSize="body-m"
                                                            >
                                                                {(acknowledgementsMap[item.announcementId] || []).length} Acknowledged
                                                            </Link>
                                                            </>
                                                        )}
                                                    </div>
                                                )}

                                                <div className="announcement-entry__tags">
                                                    {(item.targetGroups || [])?.map((tag, i) => (
                                                        <Badge key={i} style={tagStyles}>
                                                            {tag?.toUpperCase()}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
                {!loading && announcements.length > visibleCount && (
                    <div data-size='sm'>
                        <Button
                            variant="inline-link"
                            ariaLabel={showAll ? 'view less' : 'view more'}
                            onClick={() => setShowAll(prev => {
                                if (prev) {
                                    setTimeout(() => sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
                                }
                                return !prev;
                            })}
                        >
                            {showAll ? 'View less' : 'View more'}
                        </Button>
                    </div>
                )}
            </div>

            {/* Delete confirmation modal */}
            {deleteTarget && (
                <Modal 
                    visible 
                    header="Delete Announcement"
                    onDismiss={() => setDeleteTarget(null)}
                    footer={
                        <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button variant="link" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                            <Button variant="primary" onClick={confirmDelete}>Delete</Button>
                        </SpaceBetween>
                        </Box>
                    }
                >
                    Are you sure you want to delete <strong>"{deleteTarget.title}"</strong>? This cannot be undone.
                </Modal>
            )}

            {/* Acknowledgements modal — supervisor+ */}
            {ackModalTarget && (
                <Modal
                    visible={true}
                    onDismiss={() => { setAckModalTarget(null); setAckModalData([]); }}
                    header={`Acknowledgements - ${ackModalTarget.title}`}
                    size="medium"
                >
                    {ackModalLoading ? (
                        <Box textAlign="center" padding="l">
                            <Spinner />
                        </Box>
                    ) : ackModalData.length === 0 ? (
                        <Box textAlign="center" padding="l" color="text-body-secondary">
                            No one has acknowledged this announcement yet.
                        </Box>
                    ) : (
                        <Table
                            items={[...ackModalData].sort((a, b) => a.alias.localeCompare(b.alias)).slice((ackModalPage - 1) * 10, ackModalPage * 10)}
                            columnDefinitions={[
                                {
                                    id: 'alias',
                                    header: 'Acknowledged By',
                                    cell: (item: AcknowledgementSummary) => item.alias,
                                    sortingField: 'alias',
                                },
                                {
                                    id: 'acknowledgedAt',
                                    header: 'Timestamp',
                                    cell: (item: AcknowledgementSummary) => new Date(item.acknowledgedAt).toLocaleString(),
                                    sortingField: 'acknowledgedAt',
                                },
                            ]}
                            stickyColumns={{ first: 1 }}
                            variant="embedded"
                            sortingDisabled
                            pagination={
                                ackModalData.length > 10
                                    ? <Pagination
                                        currentPageIndex={ackModalPage}
                                        pagesCount={Math.ceil(ackModalData.length / 10)}
                                        onChange={({ detail }) => setAckModalPage(detail.currentPageIndex)}
                                      />
                                    : undefined
                            }
                            empty={
                                <Box textAlign="center" padding="l">
                                    No acknowledgements yet.
                                </Box>
                            }
                        />
                    )}
                </Modal>
            )}
        </div>
    );
};

export default Announcements;